import { Link } from "react-router-dom";
import { Instagram, Facebook } from "lucide-react";
import { site } from "@/config/site";

const columns = [
  {
    heading: "Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Our Approach", to: "/approach" },
      { label: "Clients", to: "/work" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    heading: "Services",
    links: [
      { label: "Business Strategy", to: "/services/business-strategy" },
      { label: "Websites", to: "/services/web-design" },
      { label: "Automation", to: "/services/marketing-automation" },
      { label: "AI Systems", to: "/services/ai-systems" },
      { label: "Brand & Messaging", to: "/services/brand-messaging" },
      { label: "Growth Advisory", to: "/services/growth-advisory" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Framework", to: "/framework" },
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms of Service", to: "/terms" },
      { label: "SMS Terms", to: "/sms-terms" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-fs-border bg-fs-bg2"
    >
      <div className="fs-shell py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center border border-fs-gold/50 font-serif text-base text-fs-gold">
                FS
              </span>
              <span className="font-serif text-xl tracking-tight text-fs-ink">
                Fortuna <span className="text-fs-gold">Strive</span>
              </span>
            </Link>
            <p className="mt-6 max-w-xs font-serif text-lg italic text-fs-muted">
              Strategy. Systems. Scale.
            </p>
            <p className="mt-2 max-w-xs text-sm text-fs-muted">
              For businesses ready to operate better.
            </p>
          </div>

          {/* Link columns */}
          {columns.map((col) => (
            <div key={col.heading} className="lg:col-span-2">
              <h4 className="mb-5 text-xs uppercase tracking-[0.24em] text-fs-gold">
                {col.heading}
              </h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-sm text-fs-muted transition-colors duration-300 hover:text-fs-ink"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Connect */}
          <div className="lg:col-span-2">
            <h4 className="mb-5 text-xs uppercase tracking-[0.24em] text-fs-gold">
              Let's Connect
            </h4>
            <ul className="space-y-3 text-sm text-fs-muted">
              <li>
                <a
                  href={`mailto:${site.contactEmail}`}
                  className="transition-colors duration-300 hover:text-fs-ink"
                  data-testid="footer-email"
                >
                  {site.contactEmail}
                </a>
              </li>
              <li className="text-fs-muted/70">{site.phone}</li>
            </ul>
            <div className="mt-5 flex gap-3">
              {[
                { icon: Instagram, href: site.social.instagram, label: "Instagram" },
                { icon: Facebook, href: site.social.facebook, label: "Facebook" },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center border border-fs-border text-fs-muted transition-colors duration-300 hover:border-fs-gold/50 hover:text-fs-gold"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-fs-border pt-8 text-xs text-fs-muted/80 sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 {site.companyLegalName}. All rights reserved.</p>
          <p>
            CIN: {site.cin} · Incorporated in {site.countryOfIncorporation}
          </p>
        </div>
      </div>
    </footer>
  );
}
